import { isCustomGraphQLError } from "@rpi/openapi-api";
import { QueryClient } from "@tanstack/react-query";

export const isNotFoundGraphQLError = (error: unknown): boolean =>
  isCustomGraphQLError(error) && error.data.extensions.code === 404;

/**
 * GraphQL 404s fail immediately and throw to the router error boundary;
 * all other errors keep the default retries and stay on the query result.
 */
export const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => !isNotFoundGraphQLError(error) && failureCount < 3,
        useErrorBoundary: isNotFoundGraphQLError,
      },
    },
  });

export const shouldRetry = (callback: () => void) => {
  return (failureCount: number, error: unknown) => {
    const shouldRetry = failureCount > 3;
    if (!isCustomGraphQLError(error)) return shouldRetry;
    if (error.data.extensions.code >= 400) {
      callback();
      return false;
    }
    return shouldRetry;
  };
};

export function formatErrorDetail(error: unknown): string | null {
  if (isCustomGraphQLError(error)) {
    const { classification, code } = error.data.extensions;
    return `${classification} (${code}): ${error.message}`;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return null;
}
